import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Card from "../components/ui/Card";
import Table from "../components/ui/Table";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";

import { FaPlus, FaFileCsv, FaFileExcel } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { HiOutlineDocumentSearch, HiOutlineMail, HiOutlineStatusOnline, HiOutlineSparkles } from "react-icons/hi";

import { fetchDashboardStats, fetchDashboardTrend, createDashboardData, type DashboardData } from "../data/dashboardData";
import LineChart from "../components/ui/charts/LineChart";
import { useScrape } from "../hooks/useScrape";
import { useAuth } from "../hooks/useAuth";
import { exportToCSV, exportToExcel, API_URL } from "../services/api";
import type { Column } from "../types/table";

interface RecentScrapeRow {
    id: string;
    account: string;
    type: "Followers" | "Following" | "Hashtag Search";
    totalEmails: number | "-";
    time: string;
    status: "completed" | "canceled" | "in-progress";
    isVerified: boolean;
}

const STAT_ICONS = [
    <HiOutlineDocumentSearch className="text-violet-500" size={22} />,
    <HiOutlineMail className="text-emerald-500" size={22} />,
    <HiOutlineStatusOnline className="text-blue-500" size={22} />,
    <HiOutlineSparkles className="text-amber-500" size={22} />,
];

const STAT_COLORS = [
    "from-violet-500/10 to-violet-500/5 border-violet-200/60",
    "from-emerald-500/10 to-emerald-500/5 border-emerald-200/60",
    "from-blue-500/10 to-blue-500/5 border-blue-200/60",
    "from-amber-500/10 to-amber-500/5 border-amber-200/60",
];

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const { jobs } = useScrape();

    // Load dashboard stats on component mount
    useEffect(() => {
        async function loadDashboardData() {
            try {
                const [stats, trend] = await Promise.all([
                    fetchDashboardStats(),
                    fetchDashboardTrend()
                ]);
                
                // Keep global user state in sync with fresh stats
                if (stats.creditsRemaining !== undefined) {
                    updateUser({ creditsRemaining: stats.creditsRemaining });
                }

                const dashboardData = createDashboardData(stats, trend);
                setData(dashboardData);
            } catch (error) {
                console.error('Failed to load dashboard data:', error);
            } finally {
                setLoading(false);
            }
        }

        loadDashboardData();

        const interval = setInterval(loadDashboardData, 30000);
        return () => clearInterval(interval);
    }, []);

        const handleStartNewScrape = () => navigate("/new");

        const handleExport = useCallback(async (type: 'csv' | 'excel', jobId: string) => {
        try {
            const token = localStorage.getItem('token');


            const response = await fetch(`${API_URL}/api/scrapes/${jobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch job details');
            const result = await response.json();
            const job = result.data;

            if (!job || !job.results || job.results.length === 0) {
                alert("No data to export.");
                return;
            }

            const flatResults = job.results.map((result: any) => ({
                target: job.target,
                scrapeType: job.scrapeType,
                username: result.username,
                email: result.email,
                isVerified: result.isVerified ? 'Yes' : 'No'
            }));

            if (type === 'csv') {
                exportToCSV(flatResults, `scrape_export_${new Date().toISOString()}.csv`);
            } else {
                exportToExcel(flatResults, `scrape_export_${new Date().toISOString()}.xlsx`);
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        }
    }, [jobs]);

        const formatTime = (timestamp: string) => {
        const now = new Date();
        const past = new Date(timestamp);
        const diffMs = now.getTime() - past.getTime();
        
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) {
            return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        } else if (diffMinutes > 0) {
            return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    const recentScrapesData: RecentScrapeRow[] = useMemo(() => {
        return jobs
            .map(job => ({
                id: job.id,
                account: job.target,
                type: (job.target.includes('#') ? 'Hashtag Search' : (job.scrapeType === 'followers' ? 'Followers' : 'Following')) as "Followers" | "Following" | "Hashtag Search",
                totalEmails: (job.resultsCount !== undefined ? (job.resultsCount || "-") : (job.results?.length || "-")) as number | "-",
                time: formatTime(job.timestamp),
                status: job.status as "completed" | "canceled" | "in-progress",
                isVerified: job.targetIsVerified || false,
            }))
            .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
            .slice(0, 5);
    }, [jobs]);

    const scrapeColumns: Column<RecentScrapeRow>[] = useMemo(
        () => [
            {
                key: "account",
                title: "Account",
                render: (row) => {
                    const cleanAccount = row.account.replace(/^@+/, '');
                    const isHashtag = cleanAccount.startsWith('#');
                    return (
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-[10px] uppercase shrink-0">
                                {isHashtag ? '#' : cleanAccount.charAt(0)}
                            </div>
                            <span className="flex items-center gap-1">
                                {(() => {
                                    const hasHash = row.account.includes('#');
                                    const text = row.account.replace(/@/g, '');
                                    return hasHash ? text : `@${text}`;
                                })()}
                                {row.isVerified && (
                                    <MdVerified className="text-blue-500" size={16} title="Verified Account" />
                                )}
                            </span>
                        </div>
                    );
                },
            },
            { key: "type", title: "Type" },
            { key: "totalEmails", title: "Total Email Scraped", headerAlign: "center", render: (row) => <div className="text-center">{row.totalEmails}</div> },
            { key: "time", title: "Time" },
            {
                key: "status",
                title: "Status",
                render: (row) => {
                    const getStatusVariant = () => {
                        if (row.status === "completed") return "completed";
                        if (row.status === "in-progress") return "running";
                        return "failed";
                    };

                    const getStatusText = () => {
                        if (row.status === "completed") return "Completed";
                        if (row.status === "in-progress") return "In Progress";
                        return "Cancelled";
                    };

                    return (
                        <Badge variant={getStatusVariant()}>
                            {getStatusText()}
                        </Badge>
                    );
                },
            },
            {
                key: "actions",
                title: "Export Emails List",
                render: (row) =>
                    row.totalEmails !== "-" && row.totalEmails > 0 ? (
                        <div className="flex flex-col gap-2">
                            <Button variant="accent" size="sm" onClick={() => handleExport('csv', row.id)}>
                                <FaFileCsv /> Export CSV
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleExport('excel', row.id)}>
                                <FaFileExcel /> Export XLS
                            </Button>
                        </div>
                    ) : null,
            },
        ],
        [jobs, handleExport]
    );
    

    return (
        <div className="p-4 md:p-6 space-y-6 bg-purple-50 min-h-screen max-w-screen">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard Overview</h1>
                    <p className="text-gray-500 text-sm">
                        Welcome back! Here&apos;s what&apos;s happening today.
                    </p>
                </div>
                <Button variant="primary" size="md" onClick={handleStartNewScrape}>
                    <FaPlus /> Start New Scrape
                </Button>
            </header>

            {/* Loading */}
            {loading ? (
                <div className="w-full h-64 flex items-center justify-center">
                    <div className="relative w-12 h-12 animate-bounce">
                        <div className="absolute inset-0 border-4 border-blue-400 rounded-full opacity-40"></div>
                        <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Stats Cards */}
                    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {data?.stats.map((s, i) => (
                            <div
                                key={s.label}
                                className={`bg-gradient-to-br ${STAT_COLORS[i]} border rounded-2xl p-5 flex flex-col gap-3 transition-all hover:shadow-md hover:-translate-y-0.5`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{s.label}</span>
                                    <div className="w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
                                        {STAT_ICONS[i]}
                                    </div>
                                </div>
                                <p className="text-3xl font-bold text-gray-900">
                                    {user?.isAdmin && s.label === "Credits Remaining" ? "Unlimited" : s.value}
                                </p>
                            </div>
                        ))}
                    </section>

                    {/* Trend Chart */}
                    <Card className="p-6">
                        <div className="mb-4">
                            <h2 className="text-lg font-bold text-gray-900 font-sans">Scraping Activity Trend</h2>
                            <p className="text-sm text-gray-500 font-sans">Number of leads extracted over the last 6 months</p>
                        </div>
                        <div className="w-full">
                            <LineChart data={data?.trend || []} tooltipLabel="Leads" height={250} />
                        </div>
                    </Card>

                    {/* Recent Scrapes */}
                    <Card className="p-4 space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-5">
                            <div className="mb-3">
                                <h2 className="text-lg font-bold text-gray-900">Recent Scrapes</h2>
                                <p className="text-sm text-gray-500">Latest scraping activity & status</p>
                            </div>
                        </div>

                            <Card className="p-0">
                            <Table<RecentScrapeRow> columns={scrapeColumns} data={recentScrapesData} />
                        </Card>
                    </Card>
                </>
            )}
        </div>
    );
}